/**
 * The clock the audio companion seeks against — one signed number, honestly derived.
 *
 * WHOSE CLOCK, AND WHO CORRECTS. This runs in the BROWSER and corrects towards the SIDECAR, which
 * is the only process in this feature that mints a timestamp. The game server has no counterpart:
 * it holds no clock, sends no probe and stamps nothing, so a mod-originated event is dated by the
 * sidecar when it lands. There was briefly a Java mirror of this file; it was deleted, because the
 * cheapest way to keep a game server's tick time is not to spend any (`AudioSocketClient`).
 *
 * WHY THIS EXISTS AT ALL. Shared music works by sending a server TIMESTAMP, not an offset: an
 * area says "this playlist started at 1754000000000" and every client walks the resource list
 * to find which clip covers *now* and seeks into it. That is what makes two players in the same
 * biome hear the same bar of the same song. It only works if each client can convert its own
 * clock into the server's, and the error in that conversion is the error in the seek. So this
 * file is load-bearing for every later phase and it is tested as such — nothing here is
 * approximate on purpose.
 *
 * THE ANSWER IS A SIGNED OFFSET, NEVER A BOOLEAN. `offsetMs` is `serverClock − localClock`: add
 * it to a local instant to get the server's. It is signed, and it is a magnitude rather than an
 * "in sync / out of sync" flag, because the Phase 0 spike found drift to be MONOTONIC and
 * ONE-DIRECTIONAL — the audio is always *behind* the server, never ahead — and the recommended
 * correction is therefore a continuous `playbackRate` nudge with a hard re-seek only past a
 * threshold. A boolean would have foreclosed that: you cannot nudge a rate towards "true". A
 * caller that wants the threshold behaviour compares the magnitude itself.
 *
 * THE MATHS is NTP's, at its smallest. One probe yields four instants:
 *
 *   T0  client sends the ping        (local clock)
 *   T1  server receives it           (server clock)   ─┐ the sidecar stamps once, so
 *   T2  server sends the pong        (server clock)   ─┘ T1 == T2 and processing is 0
 *   T3  client receives the pong     (local clock)
 *
 *   offset = ((T1 − T0) + (T2 − T3)) / 2   which with T1 == T2 is   T1 − (T0 + T3) / 2
 *   rtt    = T3 − T0
 *
 * The halving assumes the two legs of the round trip took the same time. They do not, and this
 * is the honest part: an asymmetry of `a` milliseconds (outbound slower than inbound by `a`)
 * biases every sample by exactly `a / 2`, and NO amount of sampling removes it, because nothing
 * in the four numbers distinguishes a slow outbound leg from a server clock that is ahead. What
 * sampling removes is NOISE — a garbage-collected tick, a Wi-Fi retransmit, a hidden tab whose
 * timer fired 400 ms late — and that is what the filtering below is for. The test suite asserts
 * the residual bound rather than pretending the bias is gone.
 */

/**
 * How many probes are kept. At {@link import('./audio').AUDIO_PING_INTERVAL_MS} (5 s) this is
 * three quarters of a minute of history: long enough that one bad sample cannot be the median,
 * short enough that a genuine clock step (an NTP correction on either machine) is absorbed
 * within a minute rather than being averaged against forever.
 *
 * ODD ON PURPOSE — an odd window has a single middle element, so the median is a real sample
 * rather than the mean of two, and one outlier can never pull it halfway.
 */
export const AUDIO_CLOCK_WINDOW = 9;

/**
 * How far above the window's best round trip a probe may be and still count.
 *
 * The fastest round trip observed is the one with the least queueing in it, so it is the closest
 * to the true network floor; a probe that took three times as long spent that extra time in a
 * queue on ONE of the two legs, which is precisely the asymmetry the halving cannot handle. So
 * slow probes are not just noisy, they are systematically biased, and dropping them is a
 * correction and not merely a tidy-up.
 *
 * Three rather than two because a mobile connection's honest jitter is easily 2x, and dropping
 * every real sample in favour of a stale median is worse than including a slightly noisy one.
 */
export const AUDIO_CLOCK_RTT_OUTLIER_FACTOR = 3;

/**
 * The floor under the outlier ceiling, in milliseconds — a probe within this much of the fastest is
 * never an outlier, whatever the ratio says.
 *
 * WHY A RATIO ALONE IS NOT ENOUGH: it has no resolution near zero. On a loopback or
 * same-datacentre link the round trips are 0–3 ms, and `Date.now()` cannot resolve better than a
 * millisecond, so a window of {0, 1, 3, 2, 3} would treat the 3 ms samples as 3× the "floor" of
 * 0 ms and discard most of the window as outliers — throwing away good data because the units ran
 * out, and leaving the estimate resting on whichever sample happened to be unmeasurable. Ten
 * milliseconds is comfortably below any real queueing delay and comfortably above clock resolution
 * and browser timer coarsening, so it separates "too small to measure" from "queued".
 *
 * IT IS NOT A LICENCE TO FABRICATE A ROUND TRIP. A sample invented with `T0 === T3` still sets the
 * floor to zero, and the ceiling then admits only 0–10 ms — which discards every honest sample on a
 * real network. So a caller with a server timestamp but no measured round trip (the `hello`) must
 * supply the instant it actually dialled, not the instant it read the frame; see the SPA channel,
 * which uses the dial instant for exactly this reason.
 */
export const AUDIO_CLOCK_RTT_TOLERANCE_MS = 10;

/**
 * Probes below this many samples are still usable but not yet trustworthy — {@link
 * AudioClock.settled} is false until here. Three is the smallest count with a meaningful
 * median.
 */
export const AUDIO_CLOCK_MIN_SAMPLES = 3;

/** One completed round trip. All four instants are epoch MILLISECONDS. */
export interface AudioClockSample {
  /** T0 — the client's clock when it sent the ping. */
  readonly sentAtMs: number;
  /** T1 == T2 — the sidecar's clock when it handled the ping. */
  readonly serverTimeMs: number;
  /** T3 — the client's clock when the pong arrived. */
  readonly receivedAtMs: number;
}

/** A sample reduced to the two numbers the filter works on. */
interface Reduced {
  readonly offsetMs: number;
  readonly rttMs: number;
}

function reduce(sample: AudioClockSample): Reduced {
  return {
    offsetMs: sample.serverTimeMs - (sample.sentAtMs + sample.receivedAtMs) / 2,
    rttMs: sample.receivedAtMs - sample.sentAtMs,
  };
}

/**
 * True for a probe that could have happened. A negative round trip means the local clock moved
 * backwards mid-probe (an NTP step, a laptop resuming from sleep, a hand-set clock); the sample
 * is meaningless rather than merely bad, so it is dropped at the door instead of being filtered
 * later. Non-finite values are dropped for the same reason a malformed uuid is refused rather
 * than passed on: it can only have come from a bug.
 */
export function isUsableAudioClockSample(sample: AudioClockSample): boolean {
  return (
    Number.isFinite(sample.sentAtMs) &&
    Number.isFinite(sample.serverTimeMs) &&
    Number.isFinite(sample.receivedAtMs) &&
    sample.receivedAtMs >= sample.sentAtMs
  );
}

/** The median of a non-empty list. Sorts a copy; the caller's array is untouched. */
export function median(values: readonly number[]): number {
  if (values.length === 0) throw new Error('median of an empty list');
  const sorted = [...values].sort((a, b) => a - b);
  const middle = sorted.length >> 1;
  // Even lengths take the mean of the two middles. The window is odd, so this is only reached
  // when the outlier filter has dropped one — a real case, not a theoretical one.
  return sorted.length % 2 === 1 ? (sorted[middle] as number) : ((sorted[middle - 1] as number) + (sorted[middle] as number)) / 2;
}

/**
 * A bounded, order-independent estimate of `serverClock − localClock`.
 *
 * BOUNDED: it holds at most {@link AUDIO_CLOCK_WINDOW} samples and drops the oldest, so a tab
 * open for a week costs nine numbers rather than a hundred thousand. There is no queue here and
 * no timer here — the caller decides when to probe, which is what lets a test drive it with a
 * fixed clock and no waiting at all.
 *
 * PURE: `add` is the only mutation and it takes four numbers. No `Date.now()`, no `performance`,
 * no socket. Every instant is supplied.
 */
export class AudioClock {
  private readonly samples: Reduced[] = [];
  private accepted = 0;
  private dropped = 0;

  /**
   * Feeds one completed round trip. Returns true when the sample was kept.
   *
   * A sample that fails {@link isUsableAudioClockSample} is counted in {@link
   * droppedSampleCount} and otherwise ignored — never thrown for, because this is fed from the
   * network and a hostile or broken peer must not be able to raise an exception in the tab's
   * message handler.
   */
  add(sample: AudioClockSample): boolean {
    if (!isUsableAudioClockSample(sample)) {
      this.dropped += 1;
      return false;
    }
    this.samples.push(reduce(sample));
    if (this.samples.length > AUDIO_CLOCK_WINDOW) this.samples.shift();
    this.accepted += 1;
    return true;
  }

  /** Samples currently in the window (at most {@link AUDIO_CLOCK_WINDOW}). */
  get sampleCount(): number {
    return this.samples.length;
  }

  /** Every sample ever accepted, window evictions included. Diagnostics only. */
  get acceptedSampleCount(): number {
    return this.accepted;
  }

  /** Samples refused as impossible. A non-zero value here is a clock that moved, not latency. */
  get droppedSampleCount(): number {
    return this.dropped;
  }

  /**
   * True once there are enough samples for the median to mean anything. A caller that seeks
   * audio should wait for this; a caller that only wants to display a number need not.
   */
  get settled(): boolean {
    return this.samples.length >= AUDIO_CLOCK_MIN_SAMPLES;
  }

  /**
   * `serverClock − localClock`, in milliseconds, SIGNED. Zero before the first sample, which is
   * the only honest answer when nothing is known and is also the identity — a caller that seeks
   * with it seeks as if the clocks agreed, which is what it would have had to do anyway.
   *
   * Positive means the server is AHEAD of this machine.
   */
  get offsetMs(): number {
    const kept = this.filtered();
    if (kept.length === 0) return 0;
    return median(kept.map((s) => s.offsetMs));
  }

  /**
   * The best round trip in the window, or null when empty — the tightest bound available on how
   * wrong {@link offsetMs} can be, since the residual error of the halving is at most half the
   * one-way asymmetry and the asymmetry is at most the round trip. A caller that wants to say
   * "synchronised to ±N ms" says `roundTripMs / 2`.
   */
  get roundTripMs(): number | null {
    if (this.samples.length === 0) return null;
    return Math.min(...this.samples.map((s) => s.rttMs));
  }

  /** Converts a local instant to the sidecar's clock. The one function callers actually want. */
  serverNow(localNowMs: number): number {
    return localNowMs + this.offsetMs;
  }

  /**
   * Converts a server instant to this machine's clock — the inverse, and the direction an area
   * seek actually needs: "the playlist started at server time X; what was that, locally?"
   */
  localTimeOf(serverTimeMs: number): number {
    return serverTimeMs - this.offsetMs;
  }

  /** Forgets everything. For a reconnect: the samples describe a socket that no longer exists. */
  reset(): void {
    this.samples.length = 0;
  }

  /**
   * The window minus the probes that queued. See {@link AUDIO_CLOCK_RTT_OUTLIER_FACTOR}.
   *
   * Falls back to the whole window if the filter would empty it, which cannot happen (the
   * fastest sample always passes its own test) but is written anyway so a future change to the
   * factor cannot produce a clock with no answer.
   */
  private filtered(): readonly Reduced[] {
    if (this.samples.length === 0) return this.samples;
    const fastest = Math.min(...this.samples.map((s) => s.rttMs));
    const ceiling = Math.max(fastest * AUDIO_CLOCK_RTT_OUTLIER_FACTOR, fastest + AUDIO_CLOCK_RTT_TOLERANCE_MS);
    const kept = this.samples.filter((s) => s.rttMs <= ceiling);
    return kept.length > 0 ? kept : this.samples;
  }
}
